import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { SelectButton } from "primereact/selectbutton";
import { Toast } from "primereact/toast";
import { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getErrorTitle } from "../../../constants/constants";
import { RouterAddress } from "../../../constants/RouterAddress";
import { ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { ReferenceType } from "../../../models/document";
import {
  appInnerCardStyle,
  appMutedTextStyle,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import ProviderSubscriptionDialog from "../../../components/general/ProviderSubscriptionDialog";
import Unauthorized from "../../../shared/pages/Unauthorized";
import ResidentEntryLogPanel from "./ResidentEntryLogPanel";
import ResidentDailyLogImportDialog from "./ResidentDailyLogImportDialog";
import { ensureCaregiverResidentOperationalAccess } from "../../caregiver/utils/caregiverResidentAccess";

type ResidentEntryLogPageContentProps = {
  expectedRole: string;
  allowSharedStaffAccess?: boolean;
  sharedMode?: boolean;
  dashboardPathOverride?: string;
  entryType: "incident" | "daily_note" | "resident_movement";
  pageTitle: string;
  pageSubtitle: string;
  sectionTitle: string;
  sectionDescription: string;
  defaultComposerVisible?: boolean;
  showComposerToggleButton?: boolean;
  compactActionBar?: boolean;
  compactDraftsAction?: boolean;
  compactComposerLabel?: string;
  hideEntriesSection?: boolean;
  loadEntriesPage: (backendAPIStore: any, residentId: string, options?: { limit?: number; nextToken?: string }) => Promise<any>;
  createEntry: (backendAPIStore: any, residentId: string, payload: Record<string, any>) => Promise<any>;
  updateEntry: (backendAPIStore: any, residentId: string, payload: Record<string, any>) => Promise<any>;
  deleteDraft: (backendAPIStore: any, residentId: string, entryLogId: string) => Promise<any>;
  canEditEntry: (entry: any, currentUserId: string) => boolean;
  enableMonthlyReport?: boolean;
  monthlyReportTitle?: string;
  monthlyReportDescription?: string;
  loadMonthlyReport?: (
    backendAPIStore: any,
    residentId: string,
    options: { reportRange: "today" | "last7Days" | "monthly"; reportMonth?: string }
  ) => Promise<any>;
  generateMonthlyReportDocument?: (
    backendAPIStore: any,
    residentId: string,
    requestBody: { dataset: any }
  ) => Promise<any>;
  allowedCompactSections?: Array<"composer" | "monthly" | "recent" | "drafts">;
  allowDrafts?: boolean;
  readOnlyArchivedResident?: boolean;
};

type CompactSection = "composer" | "monthly" | "recent" | "drafts";

const escapeReportHtml = (value: any) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const getReportEntryTitle = (entry: any, entryType: "incident" | "daily_note" | "resident_movement") => {
  const title = String(entry?.title || "").trim();
  const normalizedTitle = title.toLowerCase();
  const isDefaultTitle = entryType === "incident"
    ? normalizedTitle === "incident report"
    : normalizedTitle === "progress note";
  return isDefaultTitle ? "" : title;
};

const getDefaultReportMonth = () => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
  });
  const parts = formatter.formatToParts(new Date());
  const year = parts.find((part) => part.type === "year")?.value || "";
  const month = parts.find((part) => part.type === "month")?.value || "";
  return year && month ? `${year}-${month}` : "";
};

const getReportMonthDateValue = (monthKey?: string) => {
  const normalizedMonthKey = String(monthKey || "").trim();
  if (!/^\d{4}-\d{2}$/.test(normalizedMonthKey)) {
    return null;
  }

  const [year, month] = normalizedMonthKey.split("-").map((value) => Number(value));
  return Number.isFinite(year) && Number.isFinite(month)
    ? new Date(Date.UTC(year, month - 1, 1, 12, 0, 0))
    : null;
};

const getReportMonthKeyFromDateValue = (value?: Date | Date[] | null) => {
  const resolvedValue = Array.isArray(value) ? value[0] : value;
  if (!(resolvedValue instanceof Date) || Number.isNaN(resolvedValue.getTime())) {
    return "";
  }

  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
  });
  const parts = formatter.formatToParts(resolvedValue);
  const year = parts.find((part) => part.type === "year")?.value || "";
  const month = parts.find((part) => part.type === "month")?.value || "";
  return year && month ? `${year}-${month}` : "";
};

const REPORT_RANGE_OPTIONS = [
  { label: "Today", value: "today" },
  { label: "Last 7 Days", value: "last7Days" },
  { label: "Monthly", value: "monthly" },
] as const;

const AFH_HUB_WEBSITE_URL = "https://www.afhhub.com/";

const sanitizeFileNameForObjectKey = (fileName: string) =>
  String(fileName || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "") || "image";

const resolveCompactSectionFromQuery = (value?: string): CompactSection | null => {
  const normalizedValue = String(value || "").trim().toLowerCase();
  if (!normalizedValue) {
    return null;
  }
  if (["new", "new-note", "new_note", "note", "composer"].includes(normalizedValue)) {
    return "composer";
  }
  if (["monthly", "monthly-report", "monthly_report", "report"].includes(normalizedValue)) {
    return "monthly";
  }
  if (["recent", "recent-reports", "recent_reports", "reports"].includes(normalizedValue)) {
    return "recent";
  }
  if (["draft", "drafts", "draft-report", "draft-reports", "draft_reports"].includes(normalizedValue)) {
    return "drafts";
  }
  return null;
};

const GENERIC_RESIDENT_LABEL = "Resident";

const resolveResidentDisplayName = (resident: any) => {
  const combinedName = `${String(resident?.firstName || "").trim()} ${String(resident?.lastName || "").trim()}`.trim();
  const explicitName = String(resident?.residentName || resident?.name || "").trim();
  return combinedName || explicitName || GENERIC_RESIDENT_LABEL;
};

const isActiveResident = (resident: any) => {
  const activeValue = resident?.active;
  if (typeof activeValue === "string") {
    return activeValue.trim().toLowerCase() !== "false";
  }
  return activeValue !== false && activeValue !== 0;
};

const isGenericResidentDisplayName = (value: string) => String(value || "").trim() === GENERIC_RESIDENT_LABEL;

const mergeResidentContext = (...contexts: any[]) => {
  const mergedContext = contexts.reduce(
    (accumulator: Record<string, any>, context: any) => ({ ...accumulator, ...(context || {}) }),
    {},
  );
  const resolvedResidentName = contexts
    .map((context) => resolveResidentDisplayName(context))
    .find((residentName) => residentName && !isGenericResidentDisplayName(residentName))
    || contexts
      .map((context) => resolveResidentDisplayName(context))
      .find(Boolean)
    || GENERIC_RESIDENT_LABEL;
  const sameFacilityResidents = contexts
    .map((context) => (Array.isArray(context?.sameFacilityResidents) ? context.sameFacilityResidents : null))
    .find((residentList) => residentList);

  return {
    ...mergedContext,
    residentName: resolvedResidentName,
    ...(sameFacilityResidents ? { sameFacilityResidents } : {}),
  };
};

const ResidentEntryLogPageContent = ({
  expectedRole,
  allowSharedStaffAccess = false,
  sharedMode = false,
  dashboardPathOverride,
  entryType,
  pageTitle,
  pageSubtitle,
  sectionTitle,
  sectionDescription,
  defaultComposerVisible = true,
  showComposerToggleButton = true,
  compactActionBar = false,
  compactDraftsAction = false,
  compactComposerLabel,
  hideEntriesSection = false,
  loadEntriesPage,
  createEntry,
  updateEntry,
  deleteDraft,
  canEditEntry,
  enableMonthlyReport = false,
  monthlyReportTitle = "Monthly Report",
  monthlyReportDescription = "Review the month and export a clean report.",
  loadMonthlyReport,
  generateMonthlyReportDocument,
  allowedCompactSections,
  allowDrafts = true,
  readOnlyArchivedResident = false,
}: ResidentEntryLogPageContentProps) => {
  const { residentId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const toast = useRef<Toast>(null);
  // Parent pages commonly provide these loaders inline. Keep the latest loader
  // without using its changing function identity as a data-load dependency.
  const loadMonthlyReportRef = useRef(loadMonthlyReport);
  const residentContextRequestKeyRef = useRef("");

  useEffect(() => {
    loadMonthlyReportRef.current = loadMonthlyReport;
  }, [loadMonthlyReport]);

  const userRoleType = authStore.getUserRoleType();
  const currentUserId = authStore.getUserId();
  const isAdminUser = authStore.isAdmin();
  const availableRoleTypes = useMemo(
    () => Array.from(new Set(
      [
        String(authStore.currentUserContext?.user?.legacyRoleType || "").trim().toLowerCase(),
        ...(Array.isArray(authStore.currentUserContext?.profile?.selectedRoleTypes)
          ? authStore.currentUserContext?.profile?.selectedRoleTypes
          : []
        ),
        ...(Array.isArray(authStore.availableRoles) ? authStore.availableRoles : []),
      ]
        .map((roleType: any) => String(roleType || "").trim().toLowerCase())
        .filter(Boolean),
    )),
    [
      authStore.availableRoles,
      authStore.currentUserContext?.profile?.selectedRoleTypes,
      authStore.currentUserContext?.user?.legacyRoleType,
    ],
  );
  const normalizedExpectedRole = String(expectedRole || "").trim().toLowerCase();
  const normalizedUserRoleType = String(userRoleType || "").trim().toLowerCase();
  const canUseSharedStaffCaregiverWorkspace = allowSharedStaffAccess
    && normalizedExpectedRole === "caregiver"
    && (!!normalizedUserRoleType || isAdminUser);
  const canAccessExpectedRole = isAdminUser
    || normalizedUserRoleType === normalizedExpectedRole
    || availableRoleTypes.includes(normalizedExpectedRole)
    || canUseSharedStaffCaregiverWorkspace;
  const [residentContext, setResidentContext] = useState<any>(null);
  const archivedReadOnly = readOnlyArchivedResident && residentContext && !isActiveResident(residentContext);
  const baseDefaultCompactSection: CompactSection = defaultComposerVisible
    ? "composer"
    : (compactDraftsAction ? "drafts" : (!hideEntriesSection ? "recent" : "monthly"));
  const availableCompactSections: CompactSection[] = archivedReadOnly
    ? ["monthly", "recent"]
    : allowedCompactSections?.length
    ? allowedCompactSections
    : ([
      "composer",
      ...(enableMonthlyReport ? ["monthly" as CompactSection] : []),
      ...(!hideEntriesSection ? ["recent" as CompactSection] : []),
      "drafts",
    ]);
  const resolvedQueryCompactSection = resolveCompactSectionFromQuery(searchParams.get("section") || "");
  const defaultCompactSection = (
    resolvedQueryCompactSection && availableCompactSections.includes(resolvedQueryCompactSection)
      ? resolvedQueryCompactSection
      : (availableCompactSections.includes(baseDefaultCompactSection)
        ? baseDefaultCompactSection
        : availableCompactSections[0] || "composer")
  );
  const monthlyReportPreviewFrameRef = useRef<HTMLIFrameElement>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [savingEntry, setSavingEntry] = useState<boolean>(false);
  const [isProviderSubscribed, setIsProviderSubscribed] = useState<boolean>(true);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState<boolean>(false);
  const [accessBlockedMessage, setAccessBlockedMessage] = useState<string>("");
  const [entries, setEntries] = useState<any[]>([]);
  const [entriesPage, setEntriesPage] = useState<number>(1);
  const [entriesPageTokens, setEntriesPageTokens] = useState<string[]>([""]);
  const [entriesNextToken, setEntriesNextToken] = useState<string>("");
  const [loadingEntries, setLoadingEntries] = useState<boolean>(false);
  const [activeCompactSection, setActiveCompactSection] = useState<CompactSection>(defaultCompactSection);
  const [showMonthlyReportSection, setShowMonthlyReportSection] = useState<boolean>(false);
  const [reportMonth, setReportMonth] = useState<string>(getDefaultReportMonth());
  const [reportRange, setReportRange] = useState<"today" | "last7Days" | "monthly">("monthly");
  const [monthlyDataset, setMonthlyDataset] = useState<any>(null);
  const [loadingMonthlyReport, setLoadingMonthlyReport] = useState<boolean>(false);
  const [downloadingMonthlyReport, setDownloadingMonthlyReport] = useState<boolean>(false);
  const [showMonthlyReportPreviewDialog, setShowMonthlyReportPreviewDialog] = useState<boolean>(false);
  const [monthlyReportPreviewHtml, setMonthlyReportPreviewHtml] = useState<string>("");
  const [showDailyLogImportDialog, setShowDailyLogImportDialog] = useState<boolean>(false);
  const showComposerSection = compactActionBar ? activeCompactSection === "composer" : defaultComposerVisible;
  const showMonthlyReport = compactActionBar ? activeCompactSection === "monthly" : showMonthlyReportSection;
  const showRecentEntriesSection = compactActionBar ? activeCompactSection === "recent" : !hideEntriesSection;
  const showDraftsSection = allowDrafts && (compactActionBar ? activeCompactSection === "drafts" : true);
  const reportSections = useMemo(
    () => Array.isArray(monthlyDataset?.sections) && monthlyDataset.sections.length
      ? monthlyDataset.sections
      : (Array.isArray(monthlyDataset?.weeks) ? monthlyDataset.weeks : []),
    [monthlyDataset],
  );
  const reportEntries = useMemo(
    () => reportSections
      .flatMap((section: any) => Array.isArray(section?.entries) ? section.entries : [])
      .slice()
      .sort((first: any, second: any) => (
        new Date(String(second?.occurredAt || 0)).getTime()
        - new Date(String(first?.occurredAt || 0)).getTime()
      )),
    [reportSections],
  );
  const hasDraftEntries = useMemo(
    () => entries.some((entry: any) => String(entry?.status || "").trim().toLowerCase() === "draft"),
    [entries],
  );
  const canUseDailyLogImport = (entryType === "daily_note" || entryType === "incident")
    && expectedRole === ROLE_TYPE_PROVIDER
    && (userRoleType === ROLE_TYPE_PROVIDER || isAdminUser);
  const dailyLogImportButtonLabel = entryType === "daily_note"
    ? "Add Previous Daily Notes"
    : "Add Previous Incident Reports";
  const dashboardPath = dashboardPathOverride || (expectedRole === ROLE_TYPE_PROVIDER ? RouterAddress.PROVIDER_HOME : RouterAddress.CAREGIVER_HOME);
  const shouldRedirectToRecentReports = (
    expectedRole === ROLE_TYPE_PROVIDER
    || (allowSharedStaffAccess && sharedMode)
  ) && (entryType === "daily_note" || entryType === "incident" || entryType === "resident_movement");
  const sameFacilityResidentOptions = useMemo(
    () => (Array.isArray(residentContext?.sameFacilityResidents) ? residentContext.sameFacilityResidents : [])
      .filter((resident: any) => isActiveResident(resident))
      .map((resident: any) => ({
        residentId: String(resident?.residentId || "").trim(),
        residentName: resolveResidentDisplayName(resident),
      }))
      .filter((resident: any) => resident.residentId && resident.residentId !== String(residentId || "").trim()),
    [residentContext?.sameFacilityResidents, residentId],
  );

  const resolveImagePreviews = async (items: any[]) => {
    if (entryType !== "incident" || !residentId || !Array.isArray(items) || !items.length) {
      return items;
    }

    return await Promise.all(items.map(async (item: any) => {
      const imageKeys = Array.isArray(item?.imageKeys) ? item.imageKeys.slice(0, 5) : [];
      const imageFileNames = Array.isArray(item?.imageFileNames) ? item.imageFileNames.slice(0, 5) : [];
      const imagePreviews = await Promise.all(imageKeys.map(async (imageKey: string, index: number) => {
        try {
          const downloadUrl = await backendAPIStore.getPresignedUrlForDownload(
            residentId,
            ReferenceType.RESIDENT,
            "",
            imageKey,
            imageFileNames[index] || `incident-image-${index + 1}.jpg`,
            true,
          );
          return {
            key: imageKey,
            fileName: imageFileNames[index] || `incident-image-${index + 1}.jpg`,
            url: downloadUrl,
          };
        } catch {
          return null;
        }
      }));

      return {
        ...item,
        imagePreviews: imagePreviews.filter(Boolean),
      };
    }));
  };

  useEffect(() => {
    const loadResidentContext = async () => {
      if (!residentId || !canAccessExpectedRole) {
        residentContextRequestKeyRef.current = "";
        return;
      }

      const requestKey = [
        residentId,
        normalizedExpectedRole,
        normalizedUserRoleType,
        isAdminUser ? "admin" : "user",
        canAccessExpectedRole ? "allowed" : "blocked",
      ].join(":");
      if (residentContextRequestKeyRef.current === requestKey) {
        return;
      }
      residentContextRequestKeyRef.current = requestKey;

      setLoading(true);
      try {
        await backendAPIStore.init();
        if (expectedRole === ROLE_TYPE_PROVIDER && !isAdminUser) {
          const subscriptionDetails = await backendAPIStore.getSubscription(authStore.getUserId(), ROLE_TYPE_PROVIDER);
          const subscribed = !!subscriptionDetails?.isSubscribed;
          setIsProviderSubscribed(subscribed);
          if (!subscribed) {
            setShowSubscriptionDialog(true);
            setResidentContext(null);
            return;
          }
        }
        if (expectedRole !== ROLE_TYPE_PROVIDER) {
          const residentAccess = await ensureCaregiverResidentOperationalAccess(
            backendAPIStore,
            residentId,
          );
          const caregiverResidentContext = await backendAPIStore.getCaregiverResidentCarePlan(
            residentId,
            entryType === "incident" ? undefined : undefined,
          );
          setResidentContext(mergeResidentContext(
            { residentId },
            residentAccess,
            caregiverResidentContext,
          ));
          setAccessBlockedMessage("");
          return;
        }
        const response = await backendAPIStore.getResidentDetails(residentId);
        const residentHomeId = String(response?.homeId || "").trim();
        const sameFacilityResidentsResponse = residentHomeId
          ? await backendAPIStore.getResidentsForHome(residentHomeId, true).catch(() => [])
          : [];
        setResidentContext(mergeResidentContext(
          response,
          {
            sameFacilityResidents: Array.isArray(sameFacilityResidentsResponse) ? sameFacilityResidentsResponse : [],
          },
        ));
        setAccessBlockedMessage("");
      } catch (error: any) {
        if (error?.caregiverAccessError) {
          setAccessBlockedMessage(error?.message || `${pageTitle} is not available right now.`);
          if (error?.code === "SUBSCRIPTION_REQUIRED") {
            setShowSubscriptionDialog(true);
            setResidentContext(null);
          }
          return;
        }
        if (error?.code === "SUBSCRIPTION_REQUIRED") {
          if (expectedRole === ROLE_TYPE_PROVIDER) {
            setIsProviderSubscribed(false);
          } else {
            setAccessBlockedMessage(`${pageTitle} is unavailable because the provider subscription is inactive.`);
          }
          setShowSubscriptionDialog(true);
          setResidentContext(null);
        } else {
          toast.current?.show({
            severity: "error",
            summary: getErrorTitle(error),
            detail: error?.message || "Unable to load resident details.",
            life: 4000,
          });
        }
      } finally {
        setLoading(false);
      }
    };

    void loadResidentContext();
  }, [backendAPIStore, canAccessExpectedRole, expectedRole, isAdminUser, normalizedExpectedRole, normalizedUserRoleType, residentId, userRoleType]);

  const reloadEntries = async (options?: { page?: number; nextToken?: string; resetPagination?: boolean }) => {
    if (!residentId || !canAccessExpectedRole) {
      return;
    }
    if (expectedRole === ROLE_TYPE_PROVIDER && !isAdminUser && !isProviderSubscribed) {
      return;
    }
    if (expectedRole !== ROLE_TYPE_PROVIDER && accessBlockedMessage) {
      return;
    }

    const targetPage = options?.resetPagination ? 1 : Math.max(1, options?.page || entriesPage);
    const targetToken = options?.resetPagination ? "" : (options?.nextToken ?? entriesPageTokens[targetPage - 1] ?? "");

    setLoadingEntries(true);
    try {
      await backendAPIStore.init();
      const response = await loadEntriesPage(backendAPIStore, residentId, {
        limit: 10,
        nextToken: targetToken,
      });

      const resolvedItems = await resolveImagePreviews(Array.isArray(response?.items) ? response.items : []);
      setEntries(resolvedItems);
      setEntriesPage(targetPage);
      setEntriesNextToken(String(response?.nextToken || ""));
      setEntriesPageTokens((previousState) => {
        if (options?.resetPagination) {
          return [""];
        }
        const nextState = previousState.slice(0, targetPage);
        nextState[targetPage - 1] = targetToken;
        return nextState;
      });
    } catch (error: any) {
      if (error?.code === "SUBSCRIPTION_REQUIRED") {
        if (expectedRole === ROLE_TYPE_PROVIDER) {
          setIsProviderSubscribed(false);
        } else {
          setAccessBlockedMessage(`${pageTitle} is unavailable because the provider subscription is inactive.`);
        }
        setShowSubscriptionDialog(true);
      } else {
        toast.current?.show({
          severity: "error",
          summary: getErrorTitle(error),
          detail: error?.message || `Unable to load ${pageTitle.toLowerCase()}.`,
          life: 4000,
        });
      }
    } finally {
      setLoadingEntries(false);
    }
  };

  useEffect(() => {
    void reloadEntries();
  }, [residentId, userRoleType, canAccessExpectedRole]);

  useEffect(() => {
    if (compactActionBar) {
      setActiveCompactSection(defaultCompactSection);
    }
  }, [compactActionBar, defaultCompactSection]);

  useEffect(() => {
    if (!compactActionBar) {
      return;
    }

    const currentSection = String(searchParams.get("section") || "").trim();
    if (currentSection === activeCompactSection) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("section", activeCompactSection);
    setSearchParams(nextSearchParams, { replace: true });
  }, [activeCompactSection, compactActionBar, searchParams, setSearchParams]);

  useEffect(() => {
    const loadMonthlyReportDataset = async () => {
      const currentLoadMonthlyReport = loadMonthlyReportRef.current;
      if (!enableMonthlyReport || !showMonthlyReport || !currentLoadMonthlyReport || !residentId || !canAccessExpectedRole) {
        return;
      }

      setLoadingMonthlyReport(true);
      try {
        await backendAPIStore.init();
        const response = await currentLoadMonthlyReport(backendAPIStore, residentId, {
          reportRange,
          reportMonth: reportRange === "monthly" ? reportMonth : undefined,
        });
        const resolvedSections = await Promise.all(((response?.sections || response?.weeks || []) as any[]).map(async (section: any) => ({
          ...section,
          entries: await resolveImagePreviews(Array.isArray(section?.entries) ? section.entries : []),
        })));
        setMonthlyDataset(response ? { ...response, sections: resolvedSections } : null);
      } catch (error: any) {
        setMonthlyDataset(null);
        if (error?.code === "SUBSCRIPTION_REQUIRED") {
          if (expectedRole === ROLE_TYPE_PROVIDER) {
            setIsProviderSubscribed(false);
          } else {
            setAccessBlockedMessage(`${pageTitle} is unavailable because the provider subscription is inactive.`);
          }
          setShowSubscriptionDialog(true);
        } else {
          toast.current?.show({
            severity: "error",
            summary: getErrorTitle(error),
            detail: error?.message || "Unable to load the monthly report.",
            life: 4000,
          });
        }
      } finally {
        setLoadingMonthlyReport(false);
      }
    };

    void loadMonthlyReportDataset();
  }, [backendAPIStore, canAccessExpectedRole, enableMonthlyReport, expectedRole, reportMonth, reportRange, residentId, showMonthlyReport, userRoleType]);

  const handleCreateEntry = async (payload: Record<string, any>) => {
    if (!residentId) {
      return;
    }

    setSavingEntry(true);
    try {
      if (expectedRole !== ROLE_TYPE_PROVIDER) {
        const residentAccess = await ensureCaregiverResidentOperationalAccess(
          backendAPIStore,
          residentId,
        );
        setResidentContext((previousValue: any) => mergeResidentContext(previousValue, residentAccess));
        setAccessBlockedMessage("");
      }
      const response = await createEntry(backendAPIStore, residentId, payload);
      await reloadEntries({ resetPagination: true });
      const notificationStatus = String(response?.notificationStatus || "").trim().toLowerCase();
      toast.current?.show({
        severity: notificationStatus === "failed" ? "warn" : "success",
        summary: `${sectionTitle} saved`,
        detail: notificationStatus === "failed"
          ? response?.notificationError || "Your note was saved, but the email notification failed."
          : (notificationStatus === "sent"
            ? "Your note was saved and emailed to the home admin/provider."
            : "Your note is saved."),
        life: notificationStatus === "failed" ? 4500 : 2500,
      });
      if (String(payload?.status || "").trim().toLowerCase() !== "draft") {
        if (shouldRedirectToRecentReports) {
          setActiveCompactSection("recent");
        } else {
          navigate(dashboardPath);
        }
      }
    } catch (error: any) {
      if (error?.caregiverAccessError) {
        setAccessBlockedMessage(error?.message || `${pageTitle} is not available right now.`);
        if (error?.code === "SUBSCRIPTION_REQUIRED") {
          setShowSubscriptionDialog(true);
        }
      }
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || `Unable to save ${sectionTitle.toLowerCase()}.`,
        life: 4000,
      });
      throw error;
    } finally {
      setSavingEntry(false);
    }
  };

  const handleUpdateEntry = async (entryLogId: string, payload: Record<string, any>) => {
    if (!residentId) {
      return;
    }

    setSavingEntry(true);
    try {
      if (expectedRole !== ROLE_TYPE_PROVIDER) {
        const residentAccess = await ensureCaregiverResidentOperationalAccess(
          backendAPIStore,
          residentId,
        );
        setResidentContext((previousValue: any) => mergeResidentContext(previousValue, residentAccess));
        setAccessBlockedMessage("");
      }
      const response = await updateEntry(backendAPIStore, residentId, { entryLogId, ...payload });
      await reloadEntries();
      const notificationStatus = String(response?.notificationStatus || "").trim().toLowerCase();
      toast.current?.show({
        severity: notificationStatus === "failed" ? "warn" : "success",
        summary: `${sectionTitle} updated`,
        detail: notificationStatus === "failed"
          ? response?.notificationError || "Your changes were saved, but the email notification failed."
          : (notificationStatus === "sent"
            ? "Your changes were saved and emailed to the home admin/provider."
            : "Your changes are saved."),
        life: notificationStatus === "failed" ? 4500 : 2500,
      });
    } catch (error: any) {
      if (error?.caregiverAccessError) {
        setAccessBlockedMessage(error?.message || `${pageTitle} is not available right now.`);
        if (error?.code === "SUBSCRIPTION_REQUIRED") {
          setShowSubscriptionDialog(true);
        }
      }
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || `Unable to update ${sectionTitle.toLowerCase()}.`,
        life: 4000,
      });
      throw error;
    } finally {
      setSavingEntry(false);
    }
  };

  const handleDeleteDraft = async (entryLogId: string) => {
    if (!residentId) {
      return;
    }

    setSavingEntry(true);
    try {
      if (expectedRole !== ROLE_TYPE_PROVIDER) {
        const residentAccess = await ensureCaregiverResidentOperationalAccess(
          backendAPIStore,
          residentId,
        );
        setResidentContext((previousValue: any) => mergeResidentContext(previousValue, residentAccess));
        setAccessBlockedMessage("");
      }
      await deleteDraft(backendAPIStore, residentId, entryLogId);
      await reloadEntries({ resetPagination: true });
      toast.current?.show({
        severity: "success",
        summary: "Draft deleted",
        detail: "Saved draft removed successfully.",
        life: 2500,
      });
    } catch (error: any) {
      if (error?.caregiverAccessError) {
        setAccessBlockedMessage(error?.message || `${pageTitle} is not available right now.`);
        if (error?.code === "SUBSCRIPTION_REQUIRED") {
          setShowSubscriptionDialog(true);
        }
      }
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to delete draft.",
        life: 4000,
      });
      throw error;
    } finally {
      setSavingEntry(false);
    }
  };

  const formatEntryText = async (requestBody: {
    text?: string;
    taskType: string;
    tone: string;
    domain: string;
    outputStyle: string;
    metadata?: Record<string, any>;
  }) => {
    try {
      return await backendAPIStore.formatAiText(requestBody);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to polish text with AI.",
        life: 4000,
      });
      throw error;
    }
  };

  const uploadIncidentImages = async (files: File[]) => {
    if (!residentId || entryType !== "incident" || !files.length) {
      return [];
    }

    await backendAPIStore.init();
    const uploadResults = await Promise.all(files.map(async (file, index) => {
      const extension = file.name.includes(".") ? `.${file.name.split(".").pop()?.toLowerCase()}` : "";
      const objectKey = `resident/${residentId}/incident-report-images/${Date.now()}-${index}-${sanitizeFileNameForObjectKey(file.name.replace(new RegExp(`${extension}$`, "i"), ""))}${extension}`;
      const presignedUrl = await backendAPIStore.getPresignedUrlForUpload(
        residentId,
        ReferenceType.RESIDENT,
        objectKey,
      );
      await backendAPIStore.uploadFileToS3(presignedUrl, file);
      const downloadUrl = await backendAPIStore.getPresignedUrlForDownload(
        residentId,
        ReferenceType.RESIDENT,
        "",
        objectKey,
        file.name,
        true,
      );
      return {
        key: objectKey,
        fileName: file.name,
        url: downloadUrl,
      };
    }));

    return uploadResults;
  };

  const buildMonthlyReportHtml = () => {
    const entriesHtml = reportEntries.length ? (entryType === "resident_movement" ? `
      <table class="entry-table" aria-label="${escapeReportHtml(monthlyReportTitle)}">
        <thead><tr><th>Date &amp; Time</th><th>Recorded By</th><th>Entry</th><th>Notes</th></tr></thead>
        <tbody>${reportEntries.map((entry: any) => {
          const isOut = String(entry?.movementType || "").trim().toLowerCase() === "out";
          return `<tr><td>${escapeReportHtml(String(entry?.occurredAtLabel || "Time not recorded"))}</td><td>${escapeReportHtml(String(entry?.sourceStaffName || entry?.recordedBy || "")) || "-"}</td><td>${isOut ? "Out" : "In"}</td><td>${escapeReportHtml(String(entry?.notes || "-"))}</td></tr>`;
        }).join("")}</tbody>
      </table>
    ` : `
      <table class="entry-table" aria-label="${escapeReportHtml(monthlyReportTitle)}">
        <tbody>
          ${reportEntries.map((entry: any) => {
            const entryTitle = getReportEntryTitle(entry, entryType);
            return `
            <tr class="entry-meta-row">
              <td><strong>Date:</strong> ${escapeReportHtml(String(entry?.occurredAtLabel || "Time not recorded"))}</td>
              <td><strong>Submitted by:</strong> ${escapeReportHtml(String(entry?.sourceStaffName || entry?.recordedBy || "")) || "-"}</td>
            </tr>
            <tr class="entry-details-row">
              <td colspan="2">
                ${entryTitle ? `<div class="entry-title">${escapeReportHtml(entryTitle)}</div>` : ""}
                ${String(entryType === "incident" ? (entry?.incidentType || "") : (entry?.shiftLabel || "")).trim()
                  ? `<div class="entry-extra"><strong>${entryType === "incident" ? "Type" : "Shift"}:</strong> ${escapeReportHtml(String(entryType === "incident" ? (entry?.incidentType || "") : (entry?.shiftLabel || "")))}</div>`
                  : ""}
                <div>${escapeReportHtml(String(entry?.details || "Not documented"))}</div>
                ${String(entry?.actionTaken || "").trim() ? `<div class="entry-extra"><strong>Support:</strong> ${escapeReportHtml(String(entry?.actionTaken || ""))}</div>` : ""}
                ${Array.isArray(entry?.involvedResidents) && entry.involvedResidents.length ? `<div class="entry-extra"><strong>Other residents involved:</strong> ${escapeReportHtml(entry.involvedResidents.map((resident: any) => String(resident?.residentName || "").trim()).filter(Boolean).join(", "))}</div>` : ""}
                ${String(entry?.notificationsMade || "").trim() ? `<div class="entry-extra"><strong>Updates:</strong> ${escapeReportHtml(String(entry?.notificationsMade || ""))}</div>` : ""}
                ${String(entry?.notes || "").trim() ? `<div class="entry-extra"><strong>Notes:</strong> ${escapeReportHtml(String(entry?.notes || ""))}</div>` : ""}
              </td>
            </tr>
          `; }).join("")}
        </tbody>
      </table>
    `) : "";

    return `
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="utf-8" />
          <title>${escapeReportHtml(`${monthlyReportTitle} - ${monthlyDataset?.resident?.name || "Resident"}`)}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; color: #0f172a; margin: 0; background: #ffffff; }
            .page { max-width: 980px; margin: 0 auto; padding: 24px; }
            h1 { margin: 0 0 0.35rem; font-size: 1.8rem; }
            h2 { margin: 0; font-size: 1.1rem; }
            .range-label { color: #64748b; margin-bottom: 1rem; }
            .summary-table, .entry-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
            .summary-table th, .summary-table td, .entry-table th, .entry-table td { border: 1px solid #cbd5e1; padding: 10px 12px; vertical-align: top; text-align: left; }
            .summary-table th, .entry-table th { background: #f8fafc; font-weight: 700; }
            .entry-meta-row td { width: 50%; background: #f8fafc; color: #334155; }
            .entry-details-row td { padding-bottom: 16px; }
            .entry-extra { color: #64748b; font-size: 0.92rem; }
            .entry-title { font-weight: 700; margin-bottom: 4px; }
            .report-footer { margin-top: 20px; text-align: center; color: #64748b; font-size: 0.9rem; }
            .report-footer a { color: #c96a16; text-decoration: underline; }
          </style>
        </head>
        <body>
          <div class="page">
            <h1>${escapeReportHtml(monthlyReportTitle)}</h1>
            <div class="range-label">${escapeReportHtml(monthlyDataset?.reportMonth || "")}</div>
            <table class="summary-table" aria-label="Report summary">
              <tbody>
                <tr>
                  <th>Resident</th>
                  <td>${escapeReportHtml(monthlyDataset?.resident?.name || "Resident")}</td>
                  <th>Date of Birth</th>
                  <td>${escapeReportHtml(monthlyDataset?.resident?.dateOfBirth || "-")}</td>
                </tr>
                <tr>
                  <th>Facility</th>
                  <td>${escapeReportHtml(monthlyDataset?.facility?.name || "-")}</td>
                  <th>Address</th>
                  <td>${escapeReportHtml(monthlyDataset?.facility?.address || "-")}</td>
                </tr>
                <tr>
                  <th>${escapeReportHtml(reportRange === "monthly" ? "Report Month" : "Report Range")}</th>
                  <td>${escapeReportHtml(monthlyDataset?.reportMonth || "-")}</td>
                  <th>Completed Entries</th>
                  <td>${escapeReportHtml(String(monthlyDataset?.totalEntries || 0))}</td>
                </tr>
                <tr>
                  ${entryType === "resident_movement" ? "" : `<th>Drafts Excluded</th><td>${escapeReportHtml(String(monthlyDataset?.draftCount || 0))}</td>`}
                  <th>Generated</th>
                  <td ${entryType === "resident_movement" ? "colspan=\"3\"" : ""}>${escapeReportHtml(new Date().toLocaleString("en-US", { timeZone: "America/Los_Angeles" }))}</td>
                </tr>
              </tbody>
            </table>
            ${entriesHtml}
            <div class="report-footer">
              Generated by AFH Hub.
              <a href="${AFH_HUB_WEBSITE_URL}" target="_blank" rel="noopener noreferrer">${escapeReportHtml(AFH_HUB_WEBSITE_URL.replace(/^https?:\/\//, "").replace(/\/+$/, ""))}</a>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const downloadMonthlyReport = async () => {
    if (!monthlyDataset || !residentId || !generateMonthlyReportDocument) {
      return;
    }

    setDownloadingMonthlyReport(true);
    try {
      const response = await generateMonthlyReportDocument(backendAPIStore, residentId, {
        dataset: monthlyDataset,
      });
      if (!response?.documentUrl) {
        throw new Error("Unable to create report document.");
      }
      window.open(String(response.documentUrl), "_blank", "noopener,noreferrer");
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to download the report document.",
        life: 4000,
      });
    } finally {
      setDownloadingMonthlyReport(false);
    }
  };

  const viewMonthlyReport = () => {
    if (!monthlyDataset) {
      return;
    }
    setMonthlyReportPreviewHtml(buildMonthlyReportHtml());
    setShowMonthlyReportPreviewDialog(true);
  };

  if (userRoleType && !canAccessExpectedRole) {
    return <Unauthorized />;
  }

  return (
    <div className="container-fluid p-3">
      <Toast ref={toast} />
      <ProviderSubscriptionDialog
        visible={showSubscriptionDialog}
        featureName={pageTitle}
        message={expectedRole === ROLE_TYPE_PROVIDER
          ? undefined
          : `${pageTitle} is unavailable because the provider subscription is inactive.`}
        showBillingButton={expectedRole === ROLE_TYPE_PROVIDER && !isAdminUser}
        onHide={() => setShowSubscriptionDialog(false)}
      />
      {residentId && canUseDailyLogImport ? (
        <ResidentDailyLogImportDialog
          visible={showDailyLogImportDialog}
          residentId={residentId}
          onHide={() => setShowDailyLogImportDialog(false)}
          onImportCompleted={async () => {
            await reloadEntries({ resetPagination: true });
          }}
        />
      ) : null}
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <div
          className="flex justify-content-between align-items-start gap-3 flex-wrap"
          style={{ width: "100%" }}
        >
          <div style={{ minWidth: 0 }}>
            {renderSurfaceHeader(
              `${residentContext?.residentName || "Resident"} - ${pageTitle}`,
              pageSubtitle,
              entryType === "incident" ? "pi pi-exclamation-circle" : "pi pi-file-edit",
            )}
          </div>
          {compactActionBar ? (
            <div className="flex align-items-center justify-content-end gap-2 flex-wrap">
              {!archivedReadOnly && activeCompactSection !== "composer" ? (
                <Button
                  type="button"
                  label={compactComposerLabel || `New ${sectionTitle}`}
                  severity="warning"
                  rounded
                  outlined
                  onClick={() => setActiveCompactSection("composer")}
                />
              ) : null}
              {enableMonthlyReport && activeCompactSection !== "monthly" ? (
                <Button
                  type="button"
                  label={monthlyReportTitle}
                  severity="warning"
                  rounded
                  outlined
                  onClick={() => setActiveCompactSection("monthly")}
                />
              ) : null}
              {!archivedReadOnly && allowDrafts && compactDraftsAction && hasDraftEntries && activeCompactSection !== "drafts" ? (
                <Button
                  type="button"
                  label="Drafts"
                  severity="warning"
                  rounded
                  outlined
                  onClick={() => setActiveCompactSection("drafts")}
                />
              ) : null}
              {!hideEntriesSection && activeCompactSection !== "recent" ? (
                <Button
                  type="button"
                  label="Recent Entries"
                  severity="warning"
                  rounded
                  outlined
                  onClick={() => setActiveCompactSection("recent")}
                />
              ) : null}
              {!archivedReadOnly && canUseDailyLogImport ? (
                <Button
                  type="button"
                  label={dailyLogImportButtonLabel}
                  icon="pi pi-upload"
                  severity="warning"
                  rounded
                  outlined
                  onClick={() => setShowDailyLogImportDialog(true)}
                />
              ) : null}
            </div>
          ) : null}
        </div>

        {loading ? (
          <div className="card flex justify-content-center p-5">
            <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
          </div>
        ) : (((expectedRole === ROLE_TYPE_PROVIDER && !isAdminUser && !isProviderSubscribed)) || accessBlockedMessage) ? (
          <div style={{ ...appInnerCardStyle, maxWidth: 760, margin: "0 auto", padding: "1rem 1.1rem" }}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>Subscription required</div>
            <div className="mt-2" style={appMutedTextStyle}>
              {accessBlockedMessage || `${pageTitle} is available for subscribed providers only.`}
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div style={{ width: "100%", maxWidth: "74rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
              {String(sectionDescription || "").trim() ? (
                <div style={{ ...appInnerCardStyle, textAlign: "center", background: "#f8fafc" }}>
                  <div style={{ fontWeight: 800, color: "#0f172a", fontSize: "1.05rem" }}>{sectionTitle}</div>
                  <div className="mt-2" style={{ ...appMutedTextStyle, maxWidth: "42rem", margin: "0 auto" }}>
                    {sectionDescription}
                  </div>
                </div>
              ) : null}

              {enableMonthlyReport && (!compactActionBar || showMonthlyReport) ? (
                <div style={appInnerCardStyle}>
                  {!compactActionBar ? (
                    <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
                      <div>
                        <div style={{ fontWeight: 800, color: "#0f172a", fontSize: "1rem" }}>{monthlyReportTitle}</div>
                        <div className="mt-1" style={{ ...appMutedTextStyle, maxWidth: "40rem" }}>
                          {monthlyReportDescription}
                        </div>
                      </div>
                      <Button
                        type="button"
                        label={showMonthlyReport ? `Hide ${monthlyReportTitle}` : `Open ${monthlyReportTitle}`}
                        severity="warning"
                        rounded
                        outlined
                        onClick={() => setShowMonthlyReportSection((previousState) => !previousState)}
                      />
                    </div>
                  ) : null}

                  {showMonthlyReport ? (
                    <div className="mt-4" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                      <div className="grid">
                        <div className="col-12 md:col-4">
                          <label style={{ fontWeight: 700, color: "#0f172a" }}>Report Range</label>
                          <div className="mt-2">
                            <SelectButton
                              value={reportRange}
                              onChange={(e) => setReportRange(e.value)}
                              options={REPORT_RANGE_OPTIONS as any}
                              optionLabel="label"
                              optionValue="value"
                              allowEmpty={false}
                            />
                          </div>
                        </div>
                        {reportRange === "monthly" ? (
                          <div className="col-12 md:col-4">
                            <label style={{ fontWeight: 700, color: "#0f172a" }}>Report Month</label>
                            <Calendar
                              value={getReportMonthDateValue(reportMonth)}
                              onChange={(event) => setReportMonth(getReportMonthKeyFromDateValue(event.value))}
                              view="month"
                              dateFormat="MM yy"
                              showIcon
                              readOnlyInput
                              className="w-full mt-2"
                              inputClassName="w-full"
                            />
                          </div>
                        ) : null}
                      <div className="col-12 md:col-8 flex align-items-end justify-content-end">
                        <div className="flex gap-2 flex-wrap justify-content-end">
                          <Button
                            type="button"
                            label="Preview Report"
                            icon="pi pi-eye"
                            severity="warning"
                            rounded
                            outlined
                            disabled={!monthlyDataset}
                            onClick={viewMonthlyReport}
                          />
                          <Button
                            type="button"
                            label="Download Word"
                            icon="pi pi-download"
                            severity="warning"
                            rounded
                            disabled={!monthlyDataset || downloadingMonthlyReport}
                            loading={downloadingMonthlyReport}
                            onClick={() => { void downloadMonthlyReport(); }}
                          />
                        </div>
                      </div>
                      </div>

                      {loadingMonthlyReport ? (
                        <div className="flex justify-content-center py-4">
                          <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
                        </div>
                      ) : monthlyDataset ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                          <div style={{ ...appInnerCardStyle, boxShadow: "none", padding: 0, overflow: "hidden" }}>
                            {entryType === "resident_movement" ? (
                              <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                                <thead>
                                  <tr>
                                    {[
                                      ["Date & Time", "24%"],
                                      ["Recorded By", "22%"],
                                      ["Entry", "14%"],
                                      ["Notes", "40%"],
                                    ].map(([label, width]) => (
                                      <th key={label} style={{ width, textAlign: "left", padding: "0.8rem 0.9rem", border: "1px solid #e2e8f0", background: "#f8fafc", color: "#334155" }}>{label}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {reportEntries.map((entry: any, index: number) => {
                                    const isOut = String(entry?.movementType || "").trim().toLowerCase() === "out";
                                    return <tr key={`movement-report-entry-${entry?.entryLogId || index}`}>
                                      <td style={{ padding: "0.75rem 0.9rem", border: "1px solid #e2e8f0", verticalAlign: "top" }}>{String(entry?.occurredAtLabel || "Time not recorded")}</td>
                                      <td style={{ padding: "0.75rem 0.9rem", border: "1px solid #e2e8f0", verticalAlign: "top" }}>{String(entry?.sourceStaffName || entry?.recordedBy || "") || "-"}</td>
                                      <td style={{ padding: "0.75rem 0.9rem", border: "1px solid #e2e8f0", verticalAlign: "top" }}><span style={{ fontWeight: 700, color: isOut ? "#991b1b" : "#166534" }}>{isOut ? "Out" : "In"}</span></td>
                                      <td style={{ padding: "0.75rem 0.9rem", border: "1px solid #e2e8f0", verticalAlign: "top", whiteSpace: "pre-wrap" }}>{String(entry?.notes || "-")}</td>
                                    </tr>;
                                  })}
                                </tbody>
                              </table>
                            ) : (
                            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                              <tbody>
                                <tr>
                                  <th style={{ width: "18%", textAlign: "left", padding: "0.8rem 0.9rem", border: "1px solid #e2e8f0", background: "#f8fafc", color: "#0f172a" }}>Resident</th>
                                  <td style={{ padding: "0.8rem 0.9rem", border: "1px solid #e2e8f0" }}>{monthlyDataset?.resident?.name || "Resident"}</td>
                                  <th style={{ width: "18%", textAlign: "left", padding: "0.8rem 0.9rem", border: "1px solid #e2e8f0", background: "#f8fafc", color: "#0f172a" }}>{reportRange === "monthly" ? "Report Month" : "Report Range"}</th>
                                  <td style={{ padding: "0.8rem 0.9rem", border: "1px solid #e2e8f0" }}>{monthlyDataset?.reportMonth || "-"}</td>
                                </tr>
                                <tr>
                                  <th style={{ textAlign: "left", padding: "0.8rem 0.9rem", border: "1px solid #e2e8f0", background: "#f8fafc", color: "#0f172a" }}>Facility</th>
                                  <td style={{ padding: "0.8rem 0.9rem", border: "1px solid #e2e8f0" }}>{monthlyDataset?.facility?.name || "-"}</td>
                                  <th style={{ textAlign: "left", padding: "0.8rem 0.9rem", border: "1px solid #e2e8f0", background: "#f8fafc", color: "#0f172a" }}>Completed Entries</th>
                                  <td style={{ padding: "0.8rem 0.9rem", border: "1px solid #e2e8f0" }}>{String(monthlyDataset?.totalEntries || 0)}</td>
                                </tr>
                                <tr>
                                  <th style={{ textAlign: "left", padding: "0.8rem 0.9rem", border: "1px solid #e2e8f0", background: "#f8fafc", color: "#0f172a" }}>Address</th>
                                  <td style={{ padding: "0.8rem 0.9rem", border: "1px solid #e2e8f0" }}>{monthlyDataset?.facility?.address || "-"}</td>
                                  <th style={{ textAlign: "left", padding: "0.8rem 0.9rem", border: "1px solid #e2e8f0", background: "#f8fafc", color: "#0f172a" }}>Drafts Excluded</th>
                                  <td style={{ padding: "0.8rem 0.9rem", border: "1px solid #e2e8f0" }}>{String(monthlyDataset?.draftCount || 0)}</td>
                                </tr>
                              </tbody>
                            </table>
                            )}
                          </div>

                          {entryType !== "resident_movement" ? <div style={{ ...appInnerCardStyle, boxShadow: "none", padding: 0, overflow: "hidden" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                              <tbody>
                                {reportEntries.map((entry: any, index: number) => {
                                  const entryTitle = getReportEntryTitle(entry, entryType);
                                  return (
                                    <Fragment key={`report-entry-${entry?.entryLogId || index}`}>
                                      <tr>
                                        <td style={{ width: "50%", padding: "0.8rem 0.9rem", border: "1px solid #e2e8f0", background: "#f8fafc", color: "#334155", verticalAlign: "top" }}>
                                          <strong>Date:</strong> {String(entry?.occurredAtLabel || "Time not recorded")}
                                        </td>
                                        <td style={{ width: "50%", padding: "0.8rem 0.9rem", border: "1px solid #e2e8f0", background: "#f8fafc", color: "#334155", verticalAlign: "top" }}>
                                          <strong>Submitted by:</strong> {String(entry?.sourceStaffName || entry?.recordedBy || "") || "-"}
                                        </td>
                                      </tr>
                                      <tr>
                                        <td colSpan={2} style={{ padding: "0.8rem 0.9rem 1rem", border: "1px solid #e2e8f0", verticalAlign: "top" }}>
                                      {entryTitle ? (
                                        <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.25rem" }}>{entryTitle}</div>
                                      ) : null}
                                      {String(entryType === "incident" ? (entry?.incidentType || "") : (entry?.shiftLabel || "")).trim() ? (
                                        <div style={{ ...appMutedTextStyle, fontSize: "0.92rem", marginBottom: "0.25rem" }}>
                                          <strong>{entryType === "incident" ? "Type" : "Shift"}:</strong> {String(entryType === "incident" ? (entry?.incidentType || "") : (entry?.shiftLabel || ""))}
                                        </div>
                                      ) : null}
                                      <div style={{ color: "#0f172a", whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{String(entry?.details || "Not documented")}</div>
                                      {String(entry?.actionTaken || "").trim() ? (
                                        <div style={{ ...appMutedTextStyle, fontSize: "0.92rem", marginTop: "0.25rem" }}>
                                          <strong>Support:</strong> {String(entry?.actionTaken || "")}
                                        </div>
                                      ) : null}
                                      {Array.isArray(entry?.involvedResidents) && entry.involvedResidents.length ? (
                                        <div style={{ ...appMutedTextStyle, fontSize: "0.92rem", marginTop: "0.25rem" }}>
                                          <strong>Other residents involved:</strong> {entry.involvedResidents.map((resident: any) => String(resident?.residentName || "").trim()).filter(Boolean).join(", ")}
                                        </div>
                                      ) : null}
                                      {String(entry?.notificationsMade || "").trim() ? (
                                        <div style={{ ...appMutedTextStyle, fontSize: "0.92rem", marginTop: "0.25rem" }}>
                                          <strong>Updates:</strong> {String(entry?.notificationsMade || "")}
                                        </div>
                                      ) : null}
                                      {String(entry?.notes || "").trim() ? (
                                        <div style={{ ...appMutedTextStyle, fontSize: "0.92rem", marginTop: "0.25rem" }}>
                                          <strong>Notes:</strong> {String(entry?.notes || "")}
                                        </div>
                                      ) : null}
                                        </td>
                                      </tr>
                                    </Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div> : null}
                        </div>
                      ) : (
                        <div style={appMutedTextStyle}>No report data found for this month.</div>
                      )}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <ResidentEntryLogPanel
                entryType={entryType}
                entries={entries}
                loading={loadingEntries}
                saving={savingEntry}
                canEditEntry={(entry) => !archivedReadOnly && canEditEntry(entry, currentUserId)}
                onCreateEntry={handleCreateEntry}
                onUpdateEntry={handleUpdateEntry}
                onDeleteDraft={archivedReadOnly ? undefined : handleDeleteDraft}
                onFormatText={formatEntryText}
                enableImages={entryType === "incident"}
                onUploadImages={entryType === "incident" ? uploadIncidentImages : undefined}
                sameFacilityResidentOptions={entryType === "incident" ? sameFacilityResidentOptions : undefined}
                readOnly={Boolean(archivedReadOnly)}
                defaultComposerVisible={compactActionBar ? showComposerSection : defaultComposerVisible}
                composerVisible={compactActionBar ? showComposerSection : undefined}
                onComposerVisibleChange={compactActionBar ? (_visible) => setActiveCompactSection("composer") : undefined}
                showComposerToggleButton={compactActionBar ? false : showComposerToggleButton}
                showDraftsSection={showDraftsSection}
                allowDrafts={allowDrafts}
                onDraftSaved={compactActionBar ? () => setActiveCompactSection("drafts") : undefined}
                hideEntriesSection={hideEntriesSection}
                defaultEntriesVisible={compactActionBar ? showRecentEntriesSection : !hideEntriesSection}
                entriesVisible={compactActionBar ? showRecentEntriesSection : undefined}
                onEntriesVisibleChange={compactActionBar ? (_visible) => setActiveCompactSection("recent") : undefined}
                showEntriesToggleButton={!compactActionBar}
                serverEntriesPage={entriesPage}
                serverEntriesHasPreviousPage={entriesPage > 1 && entriesPageTokens.length > 1}
                serverEntriesHasNextPage={Boolean(entriesNextToken)}
                onEntriesPreviousPage={
                  entriesPage > 1
                    ? () => {
                      void reloadEntries({
                        page: entriesPage - 1,
                        nextToken: entriesPageTokens[entriesPage - 2] || "",
                      });
                    }
                    : undefined
                }
                onEntriesNextPage={
                  entriesNextToken
                    ? () => {
                      const nextPage = entriesPage + 1;
                      setEntriesPageTokens((previousState) => {
                        const nextState = previousState.slice(0, nextPage);
                        nextState[nextPage - 1] = entriesNextToken;
                        return nextState;
                      });
                      void reloadEntries({
                        page: nextPage,
                        nextToken: entriesNextToken,
                      });
                    }
                    : undefined
                }
              />
            </div>
          </div>
        )}
      </div>
      <Dialog
        header={renderSurfaceHeader(monthlyReportTitle, "Review the report before downloading the Word document.", "pi pi-file")}
        visible={showMonthlyReportPreviewDialog}
        modal
        maximizable
        style={{ width: "98vw", maxWidth: "98vw" }}
        contentStyle={{ height: "calc(100vh - 9rem)", padding: "0.75rem" }}
        onHide={() => setShowMonthlyReportPreviewDialog(false)}
        footer={(
          <div className="flex justify-content-end gap-2 flex-wrap">
            <Button
              type="button"
              label="Close"
              severity="secondary"
              outlined
              rounded
              onClick={() => setShowMonthlyReportPreviewDialog(false)}
            />
            <Button
              type="button"
              label="Download Word"
              icon="pi pi-download"
              severity="warning"
              rounded
              disabled={!monthlyDataset || downloadingMonthlyReport}
              loading={downloadingMonthlyReport}
              onClick={() => { void downloadMonthlyReport(); }}
            />
          </div>
        )}
      >
        <iframe
          ref={monthlyReportPreviewFrameRef}
          title={`${monthlyReportTitle} preview`}
          srcDoc={monthlyReportPreviewHtml}
          style={{
            width: "100%",
            height: "100%",
            minHeight: "72vh",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            background: "#ffffff",
          }}
        />
      </Dialog>
    </div>
  );
};

export default ResidentEntryLogPageContent;
